import React from 'react';

const URL_RE = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s]*)/gi;
const SWEDISH_PHONE_RE = /\b0[67]\d{8}\b/g;

export type LinkifyPart =
  | { type: 'text'; value: string }
  | { type: 'link'; value: string }
  | { type: 'phone'; value: string };

function findNextMatch(
  text: string,
  from: number,
  linkifyPhones: boolean,
): { index: number; length: number; part: LinkifyPart } | null {
  URL_RE.lastIndex = from;
  const urlMatch = URL_RE.exec(text);

  let phoneMatch: RegExpExecArray | null = null;
  if (linkifyPhones) {
    SWEDISH_PHONE_RE.lastIndex = from;
    phoneMatch = SWEDISH_PHONE_RE.exec(text);
  }

  const candidates: Array<{ index: number; length: number; part: LinkifyPart }> = [];
  if (urlMatch?.[1]) {
    candidates.push({
      index: urlMatch.index,
      length: urlMatch[0].length,
      part: { type: 'link', value: urlMatch[1] },
    });
  }
  if (phoneMatch?.[0]) {
    candidates.push({
      index: phoneMatch.index,
      length: phoneMatch[0].length,
      part: { type: 'phone', value: phoneMatch[0] },
    });
  }

  if (!candidates.length) return null;
  candidates.sort((a, b) => a.index - b.index);
  return candidates[0] ?? null;
}

export function parseLinkifyParts(text: string, linkifyPhones = false): LinkifyPart[] {
  if (!text) return [];

  const parts: LinkifyPart[] = [];
  let last = 0;

  while (last < text.length) {
    const match = findNextMatch(text, last, linkifyPhones);
    if (!match || match.index >= text.length) break;

    if (match.index > last) {
      parts.push({ type: 'text', value: text.slice(last, match.index) });
    }

    parts.push(match.part);
    last = match.index + match.length;
  }

  if (last < text.length) {
    parts.push({ type: 'text', value: text.slice(last) });
  }

  return parts.length ? parts : [{ type: 'text', value: text }];
}

export function LinkifyMessage({
  text,
  className = 'whitespace-pre-wrap break-words',
  linkClassName = 'text-cyan-400 underline break-all',
  phoneClassName = 'text-purple-300 hover:text-purple-200 underline underline-offset-2',
  linkifyPhones = false,
}: {
  text: string;
  className?: string;
  linkClassName?: string;
  phoneClassName?: string;
  linkifyPhones?: boolean;
}) {
  const parts = parseLinkifyParts(text, linkifyPhones);
  return (
    <span className={className}>
      {parts.map((p, i) => {
        if (p.type === 'link') {
          return (
            <a
              key={i}
              href={p.value}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClassName}
            >
              {p.value}
            </a>
          );
        }
        if (p.type === 'phone') {
          return (
            <a key={i} href={`tel:${p.value}`} className={phoneClassName}>
              {p.value}
            </a>
          );
        }
        return <span key={i}>{p.value}</span>;
      })}
    </span>
  );
}
